import * as ngCore from '@angular/core'

import { Store } from '../store'

import * as columnHeaders from './column-headers'
import * as rows from './rows'
import * as status from './status'

/** Owns the scroll viewport and reports its available width to the table store. */
@ngCore.Component({
  imports: [columnHeaders.Component, rows.Component, status.Component],
  selector: 'atn-ds-table-data',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** Shared table state used by the template. */
  public readonly store = ngCore.inject(Store)

  /** Rendered scroll viewport whose width constrains the column tracks. */
  private readonly _viewportSg =
    ngCore.viewChild<ngCore.ElementRef<HTMLElement>>('viewport')

  /**
   * Observes container changes for layouts that resize without a window event.
   *
   * The observer belongs to the rendered viewport rather than the window so
   * split panes, overlays, and responsive widget containers all trigger the
   * same host-width update.
   */
  constructor() {
    ngCore.effect(onCleanup => {
      const viewport = this._viewportSg()
      if (!viewport) {
        // The view child is unavailable during the first effect pass. Angular
        // reruns the effect once the viewport is rendered.
        return
      }

      const element = viewport.nativeElement
      const recordWidth = () => this.store.setHostWidth(element.clientWidth)
      const resizeObserver = new ResizeObserver(recordWidth)

      // Measure immediately instead of waiting for the first resize event.
      recordWidth()
      resizeObserver.observe(element)

      onCleanup(() => {
        // A changed view child or destroyed component must not retain an
        // observer that dispatches into an obsolete store instance.
        resizeObserver.disconnect()
      })
    })
  }

  /** Records horizontal scrolling so row tools remain at the viewport edge. */
  public viewportScrollHandler(event: Event): void {
    const viewport = event.currentTarget
    if (viewport instanceof HTMLElement) {
      this.store.setHorizontalScrollLeft(viewport.scrollLeft)
    }
  }
}
